import { getTpmsTireColor, TireStatus } from 'utils/tpms';
import TireTooltip from 'components/Tire/TireTooltip';
import BatteryIndicator from 'components/Tire/BatteryIndicator';
import type { TireInfo } from 'components/Tire/TireTooltip';

interface TireAlert {
  icon: string;
  color: string;
}

interface TireData {
  p1?: TireInfo;
  p2?: TireInfo;
  p3?: TireInfo;
  p4?: TireInfo;
  p5?: TireInfo;
  p6?: TireInfo;
}

interface Values {
  p1Status?: TireStatus;
  p2Status?: TireStatus;
  p3Status?: TireStatus;
  p4Status?: TireStatus;
  p5Status?: TireStatus;
  p6Status?: TireStatus;
  p1: number | null;
  p2: number | null;
  p3: number | null;
  p4: number | null;
  p5: number | null;
  p6: number | null;
  p1HasSensor?: boolean;
  p2HasSensor?: boolean;
  p3HasSensor?: boolean;
  p4HasSensor?: boolean;
  p5HasSensor?: boolean;
  p6HasSensor?: boolean;
  isDataStale?: boolean;
  p1IsStale?: boolean;
  p2IsStale?: boolean;
  p3IsStale?: boolean;
  p4IsStale?: boolean;
  p5IsStale?: boolean;
  p6IsStale?: boolean;
}

// Reversed layout of Inclinometer/Schematics/SixWheels.tsx ('6x4'): the
// dual/quad axle is at the front (top) and the single/duo axle is at the
// rear (bottom) — the reach-stacker '4x6' pattern.
const FourSixWheels = ({
  values,
  tireData,
  batteryLow,
  width = 86,
  height = 119,
  tireStatusMap,
  tireStaleMap,
  tireAlertMap,
}: {
  values: Values;
  tireData?: TireData;
  batteryLow?: {
    p1?: boolean;
    p2?: boolean;
    p3?: boolean;
    p4?: boolean;
    p5?: boolean;
    p6?: boolean;
  };
  width?: number | string;
  height?: number | string;
  tireStatusMap?: {
    p1?: TireStatus;
    p2?: TireStatus;
    p3?: TireStatus;
    p4?: TireStatus;
    p5?: TireStatus;
    p6?: TireStatus;
  };
  tireStaleMap?: Record<string, boolean>;
  tireAlertMap?: Record<string, TireAlert>;
}) => {
  const {
    p1Status,
    p2Status,
    p3Status,
    p4Status,
    p5Status,
    p6Status,
    p1,
    p2,
    p3,
    p4,
    p5,
    p6,
    p1HasSensor,
    p2HasSensor,
    p3HasSensor,
    p4HasSensor,
    p5HasSensor,
    p6HasSensor,
    isDataStale,
    p1IsStale,
    p2IsStale,
    p3IsStale,
    p4IsStale,
    p5IsStale,
    p6IsStale,
  } = values;

  const renderTireAlert = (key: string, x: number, y: number) => {
    const alert = tireAlertMap?.[key];
    if (!alert) return null;

    const isPlayIcon = alert.icon.includes('▶');
    const alertFontSize = isPlayIcon ? (alert.icon.length > 1 ? 6 : 9) : 16;

    return (
      <g style={{ pointerEvents: 'none' }}>
        <circle
          cx={x}
          cy={y}
          r="8"
          fill={alert.color}
          stroke="#FFFFFF"
          strokeWidth="1.8"
        />
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#FFFFFF"
          fontSize={alertFontSize}
          fontWeight="900"
        >
          {alert.icon}
        </text>
      </g>
    );
  };

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 96 119"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line
        x1="7.38462"
        y1="96.7384"
        x2="88.6154"
        y2="96.7384"
        stroke="#4A5C72"
        strokeWidth="7.38462"
      />

      {/* p1 - Left-Front-Outside (0x16) */}
      <TireTooltip tireInfo={tireData?.p1}>
        <g>
          <rect
            y="0"
            width="14.7692"
            height="44.3077"
            rx="2.80697"
            fill={getTpmsTireColor(
              tireStatusMap?.p1 ?? p1Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p1 ?? p1IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={0 + 14.7692 / 2}
            cy={0 + 44.3077 / 2}
            tireBottomY={44.3077}
            tireWidth={14.7692}
            show={batteryLow?.p1}
          />
          {renderTireAlert('p1', 14.7692 / 2, 44.3077 / 2)}
        </g>
      </TireTooltip>

      {/* p2 - Left-Front-Inside (0x17) */}
      <TireTooltip tireInfo={tireData?.p2}>
        <g>
          <rect
            x="22.1538"
            width="14.7692"
            height="44.3077"
            rx="2.80697"
            fill={getTpmsTireColor(
              tireStatusMap?.p2 ?? p2Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p2 ?? p2IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={22.1538 + 14.7692 / 2}
            cy={0 + 44.3077 / 2}
            tireBottomY={44.3077}
            tireWidth={14.7692}
            show={batteryLow?.p2}
          />
          {renderTireAlert('p2', 22.1538 + 14.7692 / 2, 44.3077 / 2)}
        </g>
      </TireTooltip>

      {/* p3 - Right-Front-Inside (0x19) */}
      <TireTooltip tireInfo={tireData?.p3}>
        <g>
          <rect
            x="59.0769"
            width="14.7692"
            height="44.3077"
            rx="2.80697"
            fill={getTpmsTireColor(
              tireStatusMap?.p3 ?? p3Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p3 ?? p3IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={59.0769 + 14.7692 / 2}
            cy={0 + 44.3077 / 2}
            tireBottomY={44.3077}
            tireWidth={14.7692}
            show={batteryLow?.p3}
          />
          {renderTireAlert('p3', 59.0769 + 14.7692 / 2, 44.3077 / 2)}
        </g>
      </TireTooltip>

      {/* p4 - Right-Front-Outside (0x1A) */}
      <TireTooltip tireInfo={tireData?.p4}>
        <g>
          <rect
            x="81.2308"
            width="14.7692"
            height="44.3077"
            rx="2.80697"
            fill={getTpmsTireColor(
              tireStatusMap?.p4 ?? p4Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p4 ?? p4IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={81.2308 + 14.7692 / 2}
            cy={0 + 44.3077 / 2}
            tireBottomY={44.3077}
            tireWidth={14.7692}
            show={batteryLow?.p4}
          />
          {renderTireAlert('p4', 81.2308 + 14.7692 / 2, 44.3077 / 2)}
        </g>
      </TireTooltip>

      {/* p5 - Left-Back (0x27) */}
      <TireTooltip tireInfo={tireData?.p5}>
        <g>
          <rect
            x="7.38462"
            y="73.8462"
            width="14.7692"
            height="44.3077"
            rx="2.80697"
            fill={getTpmsTireColor(
              tireStatusMap?.p5 ?? p5Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p5 ?? p5IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={7.38462 + 14.7692 / 2}
            cy={73.8462 + 44.3077 / 2}
            tireBottomY={118.1539}
            tireWidth={14.7692}
            show={batteryLow?.p5}
          />
          {renderTireAlert('p5', 7.38462 + 14.7692 / 2, 73.8462 + 44.3077 / 2)}
        </g>
      </TireTooltip>

      {/* p6 - Right-Back (0x29) */}
      <TireTooltip tireInfo={tireData?.p6}>
        <g>
          <rect
            x="73.8462"
            y="73.8462"
            width="14.7692"
            height="44.3077"
            rx="2.80697"
            fill={getTpmsTireColor(
              tireStatusMap?.p6 ?? p6Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p6 ?? p6IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={73.8462 + 14.7692 / 2}
            cy={73.8462 + 44.3077 / 2}
            tireBottomY={118.1539}
            tireWidth={14.7692}
            show={batteryLow?.p6}
          />
          {renderTireAlert('p6', 73.8462 + 14.7692 / 2, 73.8462 + 44.3077 / 2)}
        </g>
      </TireTooltip>

      <line
        x1="22.1538"
        y1="18.4615"
        x2="73.8462"
        y2="18.4615"
        stroke="#4A5C72"
        strokeWidth="7.38462"
      />
      <line
        x1="48"
        y1="22.1538"
        x2="48"
        y2="96"
        stroke="#4A5C72"
        strokeWidth="7.38462"
      />
    </svg>
  );
};

export default FourSixWheels;
